import { Grid, CircularProgress } from '@material-ui/core';
import { getDate, getMonth, getYear } from 'date-fns';
import React, { useContext } from 'react';
import { Redirect, useRouteMatch } from 'react-router-dom';
import { MTSView, ReportingFrequency } from 'tuapath-common/generated/schema';
import { ChildRoutes, SiteContext, StudentContext } from '../../contexts';
import MTSHelpers from '../../helpers/mtsHelpers';
import { useUserMTSPeriods } from '../../hooks';
import { GeneralErrorMessage, LanguageString } from '../Common';

export const MTS: React.FC = () => {
  const siteCtx = useContext(SiteContext);
  const stdCtx = useContext(StudentContext);
  const match = useRouteMatch();

  const mtsPeriodResults = useUserMTSPeriods();
  const mtsPeriods = mtsPeriodResults.data?.site.mtsPeriods ?? [];

  if (mtsPeriodResults.loading) {
    return (
      <Grid container justify="center" alignItems="center" style={{ minHeight: 400 }}>
        <Grid item>
          <CircularProgress color="primary" />
        </Grid>
      </Grid>
    );
  } else if (mtsPeriods.length <= 0 && siteCtx.site?.mtsPeriodCustomPropertyTypeId && siteCtx.site?.mtsPeriodsEnabled) {
    return (
      <Grid container justify="center" alignItems="center" style={{ minHeight: 400 }}>
        <Grid item>
          <GeneralErrorMessage
            message={
              <LanguageString groupName="MTS" resourceName="MTS_ERROR" defaultText="Oops, looks like your MTS periods are misconfigured" />
            }
          />
        </Grid>
      </Grid>
    );
  }

  const currentPeriod = MTSHelpers.getCurrentMTSPeriod(mtsPeriods);

  if (match.isExact) {
    const date = new Date();

    if (siteCtx.site?.pathMtsViewSelection && stdCtx?.path?.mtsView) {
      switch (stdCtx.path.mtsView) {
        case MTSView.custom:
          if (currentPeriod) {
            return <Redirect to={`${match.path}/custom/${currentPeriod.id}`} />;
          } else {
            return <Redirect to={`${match.path}/month/${getMonth(date) + 1}/${getYear(date)}`} />;
          }
          break;
        case MTSView.grouped:
          return <Redirect to={`${match.path}/custom-week/${getMonth(date) + 1}/${getDate(date)}/${getYear(date)}`} />;
        case MTSView.customgrouped:
          if (currentPeriod?.group?.id) {
            return <Redirect to={`${match.path}/customgroup/${currentPeriod.group.id}`} />;
          } else {
            return <Redirect to={`${match.path}/month/${getMonth(date) + 1}/${getYear(date)}`} />;
          }
          break;
        case MTSView.week:
          return <Redirect to={`${match.path}/week/${getMonth(date) + 1}/${getDate(date)}/${getYear(date)}`} />;
        case MTSView.month:
          return <Redirect to={`${match.path}/month/${getMonth(date) + 1}/${getYear(date)}`} />;
        case MTSView.year:
          return <Redirect to={`${match.path}/year/${getYear(date)}`} />;
        case MTSView.customReportingPeriod:
          if (currentPeriod) {
            return <Redirect to={`${match.path}/customreportingperiod/${currentPeriod.id}`} />;
          } else {
            return <Redirect to={`${match.path}/month/${getMonth(date) + 1}/${getYear(date)}`} />;
          }
          break;
        case MTSView.customReportingWeek:
          if (currentPeriod) {
            return <Redirect to={`${match.path}/customreportingweek/${getMonth(date) + 1}/${getDate(date)}/${getYear(date)}`} />;
          } else {
            return <Redirect to={`${match.path}/month/${getMonth(date) + 1}/${getYear(date)}`} />;
          }
          break;
        case MTSView.customReportingMonth:
          if (currentPeriod) {
            return <Redirect to={`${match.path}/customreportingmonth/${getMonth(date) + 1}/${getYear(date)}`} />;
          } else {
            return <Redirect to={`${match.path}/month/${getMonth(date) + 1}/${getYear(date)}`} />;
          }
          break;
        default:
          break;
      }
    }

    if (siteCtx.site?.mtsNonDailyReporting) {
      if (currentPeriod) {
        if (stdCtx?.path?.mtsView === MTSView.custom) {
          return <Redirect to={`${match.path}/custom/${currentPeriod.id}`} />;
        } else {
          return <Redirect to={`${match.path}/customreportingperiod/${currentPeriod.id}`} />;
        }
      } else {
        if (stdCtx?.path?.mtsView === MTSView.customReportingMonth && stdCtx?.path?.reportingFrequency === ReportingFrequency.MONTHLY) {
          return <Redirect to={`${match.path}/customreportingmonth/${getMonth(date) + 1}/${getYear(date)}`} />;
        } else if (stdCtx?.path?.mtsView === MTSView.customReportingWeek && stdCtx?.path?.reportingFrequency === ReportingFrequency.WEEKLY) {
          return <Redirect to={`${match.path}/customreportingweek/${getMonth(date) + 1}/${getDate(date)}/${getYear(date)}`} />;
        } else {
          return <Redirect to={`${match.path}/month/${getMonth(date) + 1}/${getYear(date)}`} />;
        }
      }
    }

    if (siteCtx.site?.defaultMtsView && !siteCtx.site?.mtsNonDailyReporting) {
      if (siteCtx.site.defaultMtsView === MTSView.week) {
        return <Redirect to={`${match.path}/week/${getMonth(date) + 1}/${getDate(date)}/${getYear(date)}`} />;
      } else if (siteCtx.site.defaultMtsView === MTSView.year) {
        return <Redirect to={`${match.path}/year/${getYear(date)}`} />;
      } else if (siteCtx.site.defaultMtsView === MTSView.custom && currentPeriod) {
        return <Redirect to={`${match.path}/${currentPeriod.id}}`} />;
      }
    }

    if (currentPeriod) {
      return <Redirect to={`${match.path}/custom/${currentPeriod.id}`} />;
    } else {
      return <Redirect to={`${match.path}/month/${getMonth(date) + 1}/${getYear(date)}`} />;
    }
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <ChildRoutes />
      </Grid>
    </Grid>
  );
};

export default MTS;
